import { UTCDate } from '@date-fns/utc';
import { TransactionType } from '@seapoint/server/enums';
import '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isSameYear } from 'date-fns';
import { ChevronDown, FileText } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../../../../design-system/button/button';
import { Checkbox } from '../../../../../design-system/checkbox/checkbox';
import { Inline } from '../../../../../design-system/inline/inline';
import { Popover } from '../../../../../design-system/popover/popover';
import { Tooltip } from '../../../../../design-system/tooltip/tooltip';
import { InvoiceEvidenceView } from '../../../../../types/entity-types';
import { cn } from '../../../../../utils/cn';
import { getAmountWithCurrency } from '../../../../../utils/get-amount-with-currency';
import { isSupportedCurrency } from '../../../../payments/utils/is-supported-currency';
import { getInvoiceDueDateLabel } from '../../../utils/get-invoice-due-date-label';
import { InvoiceTableRowOptionsMenu } from '../invoice-table-row-options-menu';
import { sortDueDate } from '../utils/sort-to-pay-invoice-due-date';
import { ConfidenceScoreBadge } from './confidence-score-badge';
import { TwoLineCell } from './two-line-cell';
import { inAnyNumberRange } from './utils/in-any-number-range-filter';
import { matchTransactionStatusFilter } from './utils/match-transaction-status-filter';

export const SELECT_COLUMN_ID = 'select';
export const IS_PAID_CONFIDENCE_SCORE_COLUMN_ID = 'isPaidConfidenceScore';
export const VIEW_INVOICE_COLUMN_ID = 'viewInvoice';
export const APPROVE_COLUMN_ID = 'approve';
export const REJECT_COLUMN_ID = 'reject';
export const DATE_COLUMN_ID = 'billingDates';
export const NAME_COLUMN_ID = 'name';

const today = new UTCDate();

const nonSecondRowColumnSizeConfig = {
  size: 0,
  minSize: 0,
};

const columnHelper = createColumnHelper<InvoiceEvidenceView>();

export const reviewMatchesTableColumnsDefinition = [
  columnHelper.display({
    id: SELECT_COLUMN_ID,
    header: 'Select',
    cell: ({ row }) => {
      if (!row.original.transactionId) {
        return (
          <Tooltip
            trigger={
              <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.original.transactionId}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                className="bg-white"
              />
            }
            content="The invoice needs to be matched to a transaction before it can be selected"
            contentAlign="start"
          />
        );
      }

      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          className="bg-white"
        />
      );
    },
    ...nonSecondRowColumnSizeConfig,
  }),
  columnHelper.accessor(IS_PAID_CONFIDENCE_SCORE_COLUMN_ID, {
    header: 'Confidence score',
    cell: ({ row }) => {
      return <ConfidenceScoreBadge confidenceScore={row.original.isPaidConfidenceScore} />;
    },
    filterFn: inAnyNumberRange,
    sortingFn: 'alphanumeric',
    ...nonSecondRowColumnSizeConfig,
  }),
  columnHelper.display({
    id: VIEW_INVOICE_COLUMN_ID,
    header: 'View invoice',
    cell: ({ row, table }) => {
      return (
        <Inline space={1.5}>
          <div className="h-4 w-4">
            <FileText strokeWidth={1} color="#86868B" className="h-full w-full" />
          </div>

          <Button
            variant="link"
            className="p-0 text-seapoint-blue-link"
            onClick={() => table.options.meta?.onReviewInvoice?.(row.original.id)}
          >
            View invoice
          </Button>
        </Inline>
      );
    },
    ...nonSecondRowColumnSizeConfig,
  }),
  columnHelper.display({
    id: APPROVE_COLUMN_ID,
    header: 'Approve',
    cell: ({ row, table }) => {
      return (
        <Button
          variant="default"
          isLoading={table.options.meta?.approvingEvidenceId?.includes(row.original.id)}
          className="py-1"
          onClick={() => table.options.meta?.onApproveInvoice?.([row.original.id])}
        >
          Approve
        </Button>
      );
    },
    ...nonSecondRowColumnSizeConfig,
  }),
  columnHelper.display({
    id: REJECT_COLUMN_ID,
    header: 'Reject',
    cell: ({ row, table }) => {
      const menuItems: ComponentProps<typeof InvoiceTableRowOptionsMenu>['menuItems'] = [
        'MATCH_TO_DIFFERENT_TRANSACTION',
        'I_HAVEN_T_PAID_THIS_INVOICE_YET',
        'DELETE_INVOICE',
      ];

      return (
        <Popover
          side="bottom"
          align="end"
          triggerElement={
            <Button
              variant="destructive"
              className="group py-1"
              iconRight={
                <ChevronDown className="h-4 w-4 transition-transform duration-200 ease-out group-data-[state=open]:rotate-180" />
              }
              onClick={() => console.log('reject')}
            >
              Reject
            </Button>
          }
          content={
            <InvoiceTableRowOptionsMenu
              menuItems={menuItems}
              onSelectMenuItem={(menuItemVariant) => {
                table.options.meta?.onSelectMenuItem?.({
                  entityId: row.original.id,
                  menuItemVariant,
                });
              }}
            />
          }
          className="min-w-[100px] overflow-clip"
        />
      );
    },
    ...nonSecondRowColumnSizeConfig,
  }),
  columnHelper.display({
    id: 'label',
    header: 'Label',
    meta: { inSecondRow: true },
    cell: ({ row, cell }) => {
      return (
        <TwoLineCell
          primary={
            <p className="text-sm font-normal leading-6 tracking-tight text-seapoint-gray-text">Invoice Information</p>
          }
          secondary={
            <p className="text-sm font-normal leading-6 tracking-tight text-seapoint-gray-text">Matched Transaction</p>
          }
          showSecondary={!!row.original.transactionId}
          align={cell.column.columnDef.meta?.align ?? 'start'}
        />
      );
    },
    size: 18,
    minSize: 18,
  }),
  columnHelper.accessor(
    (row) =>
      getInvoiceDueDateLabel({
        dueDate: row.billingDates?.dueDate,
        invoiceDate: row.billingDates?.invoiceDate,
      }),
    {
      id: DATE_COLUMN_ID,
      header: 'Billing dates',
      meta: { inSecondRow: true },
      cell: ({ row, cell }) => {
        const dueDate = getInvoiceDueDateLabel({
          dueDate: row.original.billingDates?.dueDate && formatDate(row.original.billingDates.dueDate),
          invoiceDate: row.original.billingDates?.invoiceDate && formatDate(row.original.billingDates.invoiceDate),
        });
        const fspDateLabel = row.original.transaction?.fspDate
          ? `Paid ${formatDate(row.original.transaction?.fspDate)}`
          : 'Unknown';

        const dueDateLabel = getBillingDatesColumnDueDateLabel(dueDate, !!row.original.billingDates?.dueDate?.trim());

        return (
          <TwoLineCell
            primary={<p className="text-sm font-normal leading-6 tracking-tight text-black">{dueDateLabel}</p>}
            secondary={
              <p
                className={cn('text-sm font-normal leading-6 tracking-tight text-black', {
                  'text-seapoint-orange': dueDateLabel !== fspDateLabel,
                })}
              >
                {fspDateLabel}
              </p>
            }
            showSecondary={!!row.original.transactionId}
            align={cell.column.columnDef.meta?.align ?? 'start'}
          />
        );
      },
      sortingFn: sortDueDate,
      size: 17,
      minSize: 17,
    },
  ),
  columnHelper.display({
    id: NAME_COLUMN_ID,
    header: 'Name',
    meta: { inSecondRow: true },
    cell: ({ row, cell }) => {
      const evidence = row.original;

      const cellContent =
        evidence.suggestedBeneficiary?.name ?? evidence.vendorInformation?.vendorCompanyName ?? 'Unknown';

      // AI vendor enrichment for a transaction is completed the transaction is linked to a vendor
      const vendorEnrichmentCompleted = !!evidence.transaction?.vendorId;

      const vendorName = vendorEnrichmentCompleted
        ? (evidence.transaction?.vendor?.name ?? '')
        : 'Fetching vendor details...';

      return (
        <TwoLineCell
          primary={<p className="text-sm font-normal leading-6 tracking-tight text-black">{cellContent}</p>}
          secondary={
            <p
              className={cn('text-sm font-normal leading-6 tracking-tight text-black', {
                'text-seapoint-orange': vendorEnrichmentCompleted && cellContent !== vendorName,
              })}
            >
              {vendorName}
            </p>
          }
          showSecondary={!!row.original.transactionId}
          align={cell.column.columnDef.meta?.align ?? 'start'}
        />
      );
    },
    filterFn: matchTransactionStatusFilter,
    size: 24,
  }),
  columnHelper.display({
    id: 'reference',
    header: 'Reference',
    meta: { inSecondRow: true },
    cell: ({ row, cell }) => {
      const invoiceReference = row.original.invoiceDetails?.invoiceReference;
      const evidenceTransaction = row.original.transaction;

      const isTransferOrDirectDebit =
        evidenceTransaction?.type === TransactionType.TRANSFER ||
        evidenceTransaction?.type === TransactionType.DIRECT_DEBIT;
      const transactionReference = isTransferOrDirectDebit ? evidenceTransaction?.reference : null;

      return (
        <TwoLineCell
          primary={<p className="text-sm font-normal leading-6 tracking-tight text-black">{invoiceReference}</p>}
          secondary={
            <p
              className={cn('text-sm font-normal leading-6 tracking-tight text-black', {
                'text-seapoint-orange': invoiceReference !== transactionReference,
              })}
            >
              {transactionReference}
            </p>
          }
          showSecondary={!!row.original.transactionId}
          align={cell.column.columnDef.meta?.align ?? 'start'}
        />
      );
    },
    size: 20,
  }),
  columnHelper.display({
    id: 'amount',
    header: 'Amount',
    meta: { inSecondRow: true, align: 'end' },
    cell: ({ row, cell }) => {
      if (
        !row.original.invoiceDetails?.totalAmount ||
        !row.original.invoiceDetails?.currency ||
        !isSupportedCurrency(row.original.invoiceDetails?.currency)
      ) {
        return <p className="text-end font-normal leading-6 tracking-tight text-black">—</p>;
      }

      const showSecondary = !!row.original.transactionId;

      const invoiceAmountValue = getAmountWithCurrency({
        amount: row.original.invoiceDetails?.totalAmount,
        currency: row.original.invoiceDetails?.currency,
        isAccountBalance: true,
      });

      const transactionAmountValue =
        !row.original.transaction?.amount ||
        !row.original.transaction?.currency ||
        !isSupportedCurrency(row.original.invoiceDetails?.currency)
          ? '—'
          : getAmountWithCurrency({
              amount: row.original.transaction?.amount,
              currency: row.original.transaction?.currency,
              isAccountBalance: true,
            });

      return (
        <TwoLineCell
          primary={
            <p className="text-right text-sm font-normal leading-6 tracking-tight text-black">{invoiceAmountValue}</p>
          }
          secondary={
            <p
              className={cn('text-right text-sm font-normal leading-6 tracking-tight text-black', {
                'text-seapoint-orange': invoiceAmountValue !== transactionAmountValue && transactionAmountValue !== '—',
              })}
            >
              {transactionAmountValue}
            </p>
          }
          showSecondary={showSecondary}
          align={cell.column.columnDef.meta?.align ?? 'start'}
        />
      );
    },
    size: 21,
  }),
];

const getBillingDatesColumnDueDateLabel = (formattedDueDate?: string, hasOriginalRowDueDate?: boolean) => {
  if (formattedDueDate === 'Unknown') {
    return formattedDueDate;
  }

  if (hasOriginalRowDueDate) {
    return `Due ${formattedDueDate}`;
  }

  return `Received ${formattedDueDate}`;
};

const formatDate = (date: string | Date) => {
  const dateFormat = isSameYear(today, date) ? 'dd MMM' : 'dd MMM yyyy';

  return format(new UTCDate(date), dateFormat);
};
